/**
 * A PRINTER PROFILE — what was MEASURED about one physical unit, and the evidence for it.
 *
 * Settings used to live under one global localStorage key, so every printer a till ever met shared
 * one cut offset, one paper mode and one QR method. The next unit inherited numbers measured on a
 * different machine and printed nothing useful, with nothing on screen to say why.
 *
 * Profiles are keyed by the device's own identity (VID:PID + serial, falling back to the product
 * string). A printer without a profile is reported as NOT SET UP rather than quietly given another
 * printer's numbers.
 */

import type { PaperType, QrMethod } from './commands';

export interface DeviceIdentity {
  vendorId: number;
  productId: number;
  productName?: string;
  manufacturerName?: string;
  serialNumber?: string;
  interfaceClass?: number;
  interfaceProtocol?: number;
}

export type CheckStatus = 'pass' | 'warn' | 'fail' | 'skipped';

export interface CheckRecord {
  status: CheckStatus;
  /** ISO time it was established. */
  at: string;
  /** What was observed — raw bytes, an operator's reading, a measured number. */
  evidence: string;
}

export interface PrinterProfile {
  key: string;
  identity: DeviceIdentity;
  /** GS I replies, type byte — whatever the unit said about itself. */
  reported: Record<string, string>;
  model?: string;

  headDots?: number;
  /** Where the paper sits on the head (dots from the head's left end) and how wide it is — measured. */
  paperLeftDots?: number;
  paperWidthDots?: number;
  qrMethod?: QrMethod;
  rasterWorks?: boolean;

  paperType?: PaperType;
  /** Which `13 74 xx 78` byte moved the cut on THIS unit (the SDK and the vendor tool disagree). */
  cutOffsetCommand?: 'cut33' | 'print11';
  /** The mark-search command this firmware honours (measured): ESC FF on the EP802, GS FF per the SDKs. */
  markFeed?: 'escff' | 'gsff';
  /** Physical facts software cannot store in the printer — e.g. where the paper sensor was slid to. */
  sensorNote?: string;
  cutOffsetUnits?: number;
  printOffsetUnits?: number;
  markThreshold?: number;
  pitchMm?: number;
  cutterGapMm?: number;
  /** Reverse-feed after a mark cut, so print starts at the ticket's top edge. 0 = firmware can't. */
  backFeedDots?: number;

  /** Print the "EVENT TICKET" + event-name block. Off for stock that already carries the event's branding. */
  showHeader?: boolean;
  /** Which ticket template prints (ticketPrinter/templates.ts). Absent = the default template. */
  template?: string;
  /** What the type slip showed this printer draws (setup step "Type & columns"). Absent = not checked. */
  typeCaps?: TypeCaps;
  /** Layout, carried with the printer instead of the till. */
  marginDots?: number;
  topDots?: number;
  tailDots?: number;

  checks: Record<string, CheckRecord>;
  /** Set when the final back-to-back ticket check passed. */
  verifiedAt?: string;
  updatedAt: string;
}

const KEY = 'masung-printer-profiles';

export function deviceKey(id: DeviceIdentity): string {
  const h = (n: number) => n.toString(16).padStart(4, '0');
  const who = id.serialNumber?.trim() || id.productName?.trim() || 'unknown';
  return `${h(id.vendorId)}:${h(id.productId)}:${who}`;
}

function readAll(): Record<string, PrinterProfile> {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
}

export function loadProfile(key: string): PrinterProfile | null {
  return readAll()[key] ?? null;
}

export function listProfiles(): PrinterProfile[] {
  return Object.values(readAll()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function saveProfile(p: PrinterProfile): PrinterProfile {
  const all = readAll();
  const next = { ...p, updatedAt: new Date().toISOString() };
  all[p.key] = next;
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
  // Every mounted printer hook (the till bar, the wizard, the page) re-reads — one source of truth.
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(PROFILE_EVENT, { detail: next.key }));
  return next;
}

export const PROFILE_EVENT = 'masung-profile-changed';

/**
 * WHAT "SET UP" MEANS — the checks a printer must have PASSED before the till calls it set up.
 * Not "the operator reached the last screen": each one is a measurement the tickets depend on.
 */
export interface TypeCaps { tall: boolean; columns: boolean; accents: boolean; rasterRule: boolean; underlineRule: boolean; serif: boolean; frame: boolean }

export function essentialChecks(p: Pick<PrinterProfile, 'paperType'>): string[] {
  const marked = p.paperType === 'mark' || p.paperType === 'gap';
  return ['capability', 'paperWrite', ...(marked ? ['paper', 'cut'] : ['blade']), 'verify'];
}

export function missingEssentials(p: PrinterProfile): string[] {
  return essentialChecks(p).filter((k) => {
    const c = p.checks[k];
    return !c || (c.status !== 'pass' && !(k === 'capability' && c.status === 'warn'));
  });
}

export const CHECK_NAMES: Record<string, string> = {
  env: 'Browser', connect: 'Connect', talk: 'Two-way talk', health: 'Health', selftest: "Printer's own settings",
  capability: 'What it can print', type: 'Type & columns', paperWrite: 'Paper type written', paper: 'Sensor sees the marks',
  blade: 'Blade distance', cut: 'Cut on the perforation', verify: 'Real tickets', imported: 'Setup imported',
};

export function deleteProfile(key: string) {
  const all = readAll();
  delete all[key];
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch {}
}

export function emptyProfile(identity: DeviceIdentity): PrinterProfile {
  return { key: deviceKey(identity), identity, reported: {}, checks: {}, updatedAt: new Date().toISOString() };
}

// ── What a USB identity tells us, and how sure we are ───────────────────────────────────────────

export interface Recognition {
  family: 'masung-kiosk' | 'unknown';
  /** One honest sentence — including when we do not know. */
  summary: string;
  /** Firmware hint from the product string: decides which QR command is LIKELY; the probe decides. */
  likelyQr?: QrMethod;
  notes: string[];
}

export function recognize(id: DeviceIdentity): Recognition {
  const notes: string[] = [];
  const name = (id.productName || '').toLowerCase();
  const masungPid = id.vendorId === 0x0519 && [0x2013, 0x2015, 0x2017].includes(id.productId);
  const stm = id.vendorId === 0x0483;
  if (id.vendorId === 0x0519) notes.push('Vendor 0x0519 is registered to Star Micronics; Masung reuses it, so the computer may call this a Star printer.');
  if (stm && id.productId === 0x7540) notes.push('0483:7540 is the firmware-upgrade bootloader. Power-cycle the printer to leave upgrade mode.');
  if (id.interfaceClass !== undefined && id.interfaceClass !== 7) notes.push(`USB interface class ${id.interfaceClass}, not 7 (printer). It may be a serial bridge or a different device.`);

  let likelyQr: QrMethod | undefined;
  if (name.includes('ip1000')) { likelyQr = 'escpos'; notes.push('"IP1000" firmware is reported to print standard ESC/POS QR codes and pictures.'); }
  else if (name.includes('masung')) { likelyQr = 'dc3'; notes.push('"Masung Printer" firmware is reported to need Masung\'s own QR command and to ignore standard pictures.'); }

  if (masungPid || name.includes('ip1000') || name.includes('masung') || (stm && id.productId !== 0x7540)) {
    return {
      family: 'masung-kiosk',
      summary: `Masung-family kiosk printer${id.productName ? ` reporting itself as "${id.productName}"` : ''}. The exact model is not encoded in USB — the setup measures what matters instead.`,
      likelyQr, notes,
    };
  }
  return {
    family: 'unknown',
    summary: `Not a printer this setup recognizes (${id.productName || 'no product name'}). It may still speak the same commands — every step below checks rather than assumes.`,
    likelyQr, notes,
  };
}
